import { IconButton } from "@/design-system/IconButton/IconButton";
import {
  MetaRatingUpgradeArrow,
  chipGradient,
} from "@/design-system/Tags/MetaRatingChip/MetaRatingChip";
import { cn } from "@/lib/utils";
import { Chemistry as ChemistryValue } from "@repo/shared/types";
import { ButtonHTMLAttributes } from "react";

const diamondColors = {
  inactive: "var(--color-neutral-500)",
  active: "var(--color-primary-action)",
  // A diamond gained or lost against previousChemistry, as in the squad builder.
  up: "var(--color-upgrade)",
  down: "var(--color-error)",
};

type ChemistryIconProps = {
  /** 0–3: diamond n lights up while chemistry ≥ n. */
  chemistry: ChemistryValue;
  /**
   * The chemistry before a change: diamonds gained show green, diamonds lost
   * red. Omitted, the icon shows chemistry alone.
   */
  previousChemistry?: ChemistryValue;
  className?: string;
};

export const ChemistryIcon = ({
  chemistry,
  previousChemistry,
  className,
}: ChemistryIconProps) => {
  const before = previousChemistry ?? chemistry;
  const fill = (diamond: number) => {
    const now = chemistry >= diamond;
    const then = before >= diamond;
    if (now && !then) return diamondColors.up;
    if (!now && then) return diamondColors.down;
    return now ? diamondColors.active : diamondColors.inactive;
  };

  return (
    <svg
      viewBox="0 0 57 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M0.958989 28L13.4663 15.4927L25.9737 28L13.4663 40.5073L0.958989 28Z"
        fill={fill(1)}
      />
      <path
        d="M16.1211 12.8359L28.6285 0.328611L41.1358 12.8359L28.6285 25.3433L16.1211 12.8359Z"
        fill={fill(2)}
      />
      <path
        d="M31.2852 28L43.7925 15.4927L56.2999 28L43.7925 40.5073L31.2852 28Z"
        fill={fill(3)}
      />
    </svg>
  );
};

type ChemistryChipProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  chemistry: ChemistryValue;
  /** Shows gained diamonds green and lost ones red (see ChemistryIcon). */
  previousChemistry?: ChemistryValue;
  /** "span" when it only shows the chemistry, as on a player card. */
  as?: "button" | "span";
};

/**
 * Chemistry diamonds inside the round dark icon-button container (the circular
 * sibling of the square CloseButton/BackButton family). A button where it
 * opens the chemistry picker; a plain chip where it only shows the value.
 */
export const ChemistryChip = ({
  chemistry,
  previousChemistry,
  className,
  style,
  ...props
}: ChemistryChipProps) => (
  <IconButton
    shape="round"
    aria-label={`Chemistry ${chemistry} of 3`}
    icon={
      <ChemistryIcon
        chemistry={chemistry}
        previousChemistry={previousChemistry}
        className="w-5"
      />
    }
    // Swaps the icon button's own border and fill for the meta rating chip's
    // gradient ring and fill, so the two chips look the same.
    className={cn(
      "border-0 bg-(--border-gradient-fill) hover:bg-(--border-gradient-fill)",
      chipGradient.className,
      className,
    )}
    style={{ ...chipGradient.style, ...style }}
    {...props}
  />
);

/** A change in chemistry, coloured and pointed like a meta rating change. */
export const ChemistryDelta = ({ value }: { value: number }) =>
  value === 0 ? null : (
    <span
      className={cn(
        "flex items-center tabular-nums",
        value < 0 ? "text-error" : "text-upgrade",
      )}
    >
      <span className={cn("flex", value < 0 && "rotate-180")}>
        <MetaRatingUpgradeArrow />
      </span>
      <span>{Math.abs(value)}</span>
    </span>
  );
